/**
 * Правило периода наблюдения: не длиннее календарного месяца и не в будущем.
 *
 * Правило живёт в дизайн-системе, а не в вызывающем коде: технически сборка
 * умеет и более длинный период, но в интерфейсе мы его не предлагаем —
 * отчёт устроен как месячный документ, и период в квартал сделал бы его
 * выводы несопоставимыми с соседними отчётами того же проекта.
 *
 * Функции чистые и принимают «сегодня» аргументом. Дата внутри превратила бы
 * проверку в непроверяемую: тест не может подождать до следующего месяца.
 *
 * @format
 */

import {formatDate, parseDate} from 'ui/components/molecules/DatePicker';

/** Сколько дней в периоде, границы включительные. */
const MS_IN_DAY = 24 * 60 * 60 * 1000;

/**
 * Прибавляет месяц, оставаясь в календаре.
 *
 * `31.01` плюс месяц — это `28.02`, а не `03.03`, как посчитал бы `Date`,
 * перекатив лишние дни в следующий месяц. Без подрезки правило вело бы себя
 * необъяснимо ровно в тех месяцах, где пользователь и ждёт подвоха.
 */
export function addMonthClamped(date: Date): Date {
  const day = date.getDate();
  const shifted = new Date(date.getFullYear(), date.getMonth() + 1, 1);
  // Нулевой день следующего месяца — последний день текущего; так длина
  // месяца считается сама, без таблицы и правила високосного года.
  const lastDay = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate();
  shifted.setDate(Math.min(day, lastDay));
  return shifted;
}

/** Целых дней между датами включительно: `01.06`–`30.06` это 30. */
export function daysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / MS_IN_DAY) + 1;
}

export interface PeriodBounds {
  /** Границы поля «Период с». */
  fromMin?: string;
  fromMax?: string;
  /** Границы поля «Период по». */
  toMin?: string;
  toMax?: string;
}

/**
 * Границы для календарей обоих полей.
 *
 * Пока второй край не задан (или набран не датой), окно от него не строится:
 * ограничивать календарь по несуществующему значению — значит гасить дни без
 * причины, которую читатель может увидеть.
 *
 * Верхняя граница у обоих полей — сегодня: отчёт собирается по прожитым
 * дням, и будущий период дал бы документ про то, чего ещё не случилось.
 */
export function periodBounds(from: string, to: string, today: Date): PeriodBounds {
  const start = parseDate(from);
  const end = parseDate(to);
  const todayLabel = formatDate(today);

  return {
    // Начало не раньше, чем за месяц до конца, и не позже самого конца. Но
    // и не позже сегодняшнего дня: конец мог быть набран руками и оказаться
    // в будущем — тогда брать верхнюю границу у него значило бы пускать
    // начало туда же.
    fromMin: end ? formatDate(subMonthClamped(end)) : undefined,
    fromMax: end ? minDate(formatDate(end), todayLabel) : todayLabel,
    // Конец не раньше начала и не дальше месяца от него; будущее отсечено
    // в любом случае.
    toMin: start ? formatDate(start) : undefined,
    toMax: start ? minDate(formatDate(addMonthClamped(start)), todayLabel) : todayLabel,
  };
}

/** Зеркало `addMonthClamped` — на месяц назад, с той же подрезкой. */
function subMonthClamped(date: Date): Date {
  const day = date.getDate();
  const shifted = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const lastDay = new Date(shifted.getFullYear(), shifted.getMonth() + 1, 0).getDate();
  shifted.setDate(Math.min(day, lastDay));
  return shifted;
}

/** Раньшая из двух дат в формате `ДД.ММ.ГГГГ`. */
function minDate(a: string, b: string): string {
  const first = parseDate(a);
  const second = parseDate(b);
  if (!first || !second) return a;
  return first <= second ? a : b;
}

/**
 * Что не так с периодом. `null` — всё в порядке.
 *
 * Формат намеренно не проверяется здесь: незаконченный ввод («01.0») это не
 * ошибка пользователя, а середина набора, и ругаться на неё, пока человек
 * печатает, — значит мигать красным в каждом поле. Про формат сообщает поле,
 * когда фокус ушло.
 */
/**
 * Сообщения о периоде.
 *
 * Вынесены словарём, а не набраны по месту: на них ссылаются тесты, и
 * сверка «текст из компонента против текста из теста» иначе превращается в
 * сверку двух копий одной строки, которые расходятся при первой же правке.
 */
export const PERIOD_MESSAGE = {
  reversed: 'Начало периода позже его конца.',
  future:
    'Период не может заканчиваться в будущем: отчёт собирается по прожитым дням.',
  tooLong: (days: number): string =>
    'Период не может быть длиннее месяца — сейчас выбрано ' + days + ' ' + dayWord(days) + '.',
} as const;

export function periodError(from: string, to: string, today: Date): string | null {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return null;

  if (start > end) return PERIOD_MESSAGE.reversed;

  const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (end > todayEnd) return PERIOD_MESSAGE.future;

  if (end > addMonthClamped(start)) return PERIOD_MESSAGE.tooLong(daysBetween(start, end));

  return null;
}
/** «день» / «дня» / «дней» — счётчик в тексте ошибки читается словами. */
function dayWord(count: number): string {
  const n = Math.abs(count) % 100;
  if (n >= 11 && n <= 14) return 'дней';
  const last = n % 10;
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}
